import React, { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import {
  FiCalendar, FiExternalLink, FiFilter, FiList, FiMap,
  FiMapPin, FiNavigation, FiRefreshCw, FiSearch, FiSettings, FiUser, FiTarget, FiLoader, FiLayers,
  FiMaximize2, FiMinimize2
} from "react-icons/fi"
import api from "../../libs/axios"
import { useAuth } from "../../provider/auth.provider"
import { SelectWithSearch, SelectWithSearch2, SearchableSelect, type SelectOption } from "../../utils/select-with-search"
import toast from "react-hot-toast"
import { dateRangeInputBounds, firstDateRangeError } from "../../utils/date-range.utils"

declare global {
  interface Window { L?: any }
}

interface SiteEngineer {
  id: number
  first_name: string
  last_name: string
  username?: string
}

// Sites come from two modules. `site_type` tells them apart: 'survey' rows are project
// sites, 'inc' rows are INC (Installation & Commissioning) sites projected into the same
// shape by the API. INC rows carry their own workflow enum in inc_workflow_status.
type SiteType = "survey" | "inc"

interface Site {
  id: number
  site_id: string
  site_type?: SiteType
  site_name?: string
  site_address?: string
  project_id: number
  site_latitude?: string | number | null
  site_longitude?: string | number | null
  site_priority?: string
  survey_workflow_status: string
  inc_workflow_status?: string | null
  work_type?: string | null
  team_type?: string | null
  assignment_count?: number
  site_engineer_userid?: number | null
  site_engineer?: SiteEngineer
  project?: { id: number; project_name: string }
  activity?: { id: number; activity_name: string } | null
  district?: { id: number; name: string } | null
  status?: string
  site_engineer_work_status?: string
  technician_name?: string | null
  fse_name?: string | null
  created_at?: string | null
}

const isIncSite = (site: Site) => site.site_type === "inc"
const siteTypeLabel = (site: Site) => (isIncSite(site) ? "INC Site" : "Survey Site")

// Sites on hold get their own pin colour regardless of stage, so a stalled site
// never reads as live work.
const HOLD_COLOR = "#1f2937" // gray-800
const isOnHold = (site: Site) => site.status === "hold"

const fmtCreatedDate = (value?: string | null) => {
  if (!value) return "—"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
}

// Engineer offered in the assign dialog, with the straight-line distance from the
// site to their reporting location (null when they have none on file).
interface EngineerSuggestion {
  id: number
  label: string
  distance_km: number | null
}

// Same roles the Project Sites table offers as site engineers.
const SITE_ENGINEER_ASSIGNABLE_ROLE_IDS = [7, 4]

/**
 * Engineer assignment from the map, mirroring the Project Sites table's rules:
 * assign while the site is only created, reassign while an engineer is on it
 * and field work has not begun. Past `engineer_assigned` the site is in
 * someone's hands and the map is not the place to take it away.
 *
 * INC sites are excluded — they carry their own supervisor workflow.
 */
const mapAssignMode = (site: Site): "assign" | "reassign" | null => {
  if (isIncSite(site)) return null
  if (site.status === "cancelled") return null
  if (site.survey_workflow_status === "site_created") return site.site_engineer_userid ? "reassign" : "assign"
  if (site.survey_workflow_status === "engineer_assigned") return site.site_engineer_userid ? "reassign" : "assign"
  return null
}

interface AttendanceRecord {
  id: number
  user_id: number
  attendance_date: string
  check_in_latitude?: number | null
  check_in_longitude?: number | null
  check_in_time?: string | null
  check_in_address?: string | null
  check_out_latitude?: number | null
  check_out_longitude?: number | null
  check_out_time?: string | null
  check_out_address?: string | null
  reporting_latitude?: number | null
  reporting_longitude?: number | null
  work_status?: string
  hr_status?: string
  user?: {
    id: number
    first_name: string
    last_name: string
    username?: string
    employee_code?: string
    reporting_latitude?: number | null
    reporting_longitude?: number | null
    district?: { id: number; name: string } | null
  }
}

interface Filters {
  site_id_list?: string
  site_id?: string; site_name?: string; site_address?: string; activity_id?: string
  reference_number?: string; project_id?: string; state_circle_id?: string; district_id?: string
  pincode?: string; opco_name?: string; opco_id?: string; created_from?: string; created_to?: string
  pr_date_from?: string; pr_date_to?: string; po_date_from?: string; po_date_to?: string
  pr_number?: string; po_number?: string; is_po_no_shared_at_allocation?: string
  pending_assignment?: string; technician_name?: string; technician_number?: string
  fse_name?: string; fse_number?: string; aom_name?: string; aom_number?: string
  site_priority?: string; survey_workflow_status?: string; workflow_status_list?: string; stage_group?: string
}

const STAGE_GROUP_MAP: Record<string, string> = {
  survey_pending: ["site_created", "engineer_assigned", "field_work_in_progress"].join(","),
  survey_done: ["field_work_completed", "ta_review_pending", "ta_review_completed", "ta_rejected", "mis_report_assign_pending", "report_maker_assigned", "report_in_progress", "report_completed", "report_maker_rejected", "validator_assigned", "validation_in_progress", "validation_completed", "validation_rejected", "report_finalized", "report_sent_to_client"].join(","),
  field_work_approved: ["ta_review_completed", "mis_report_assign_pending", "report_maker_assigned", "report_in_progress", "report_completed", "report_maker_rejected", "validator_assigned", "validation_in_progress", "validation_completed", "validation_rejected", "report_finalized", "report_sent_to_client"].join(","),
  report_completed: ["report_completed", "report_maker_rejected", "validator_assigned", "validation_in_progress", "validation_completed", "validation_rejected", "report_finalized", "report_sent_to_client"].join(","),
  report_finalized: ["report_finalized", "report_sent_to_client"].join(","),
  report_preparation_pending: ["field_work_completed", "ta_review_completed", "report_maker_assigned"].join(","),
  validation_pending: ["report_completed", "validator_assigned"].join(","),
  report_submission_pending: ["field_work_completed", "ta_review_completed", "report_maker_assigned", "report_completed", "validator_assigned", "report_finalized"].join(","),
}

const STAGE_LABELS: Record<string, string> = {
  survey_pending: "Survey Pending", survey_done: "Survey Done", field_work_approved: "Field Work Approved",
  report_completed: "Report Completed", report_finalized: "Report Finalized",
  report_preparation_pending: "Report Preparation Pending", validation_pending: "Validation Pending",
  report_submission_pending: "Report Submission Pending",
}

// Distinct colour per workflow stage group (for Leaflet markers)
// Engineer Assigned group → cyan | Site WIP group → orange | TA Review → purple
// Report Maker group → yellow | Validation group → teal/emerald | Finalized → rose/pink
const STAGE_COLORS: Record<string, string> = {
  site_created: "#94a3b8",            // slate-400 (grey, pre-assignment)
  engineer_assigned: "#06b6d4",       // cyan-500
  field_work_in_progress: "#f97316",  // orange-500
  data_uploaded: "#ea580c",           // orange-600
  field_work_completed: "#fb923c",    // orange-400
  ta_review_pending: "#a78bfa",       // violet-400
  ta_review_completed: "#8b5cf6",     // violet-500
  ta_rejected: "#7c3aed",             // violet-600
  mis_report_assign_pending: "#fbbf24", // amber-400
  report_maker_assigned: "#f59e0b",   // amber-500
  report_in_progress: "#d97706",      // amber-600
  report_completed: "#b45309",        // amber-700
  report_maker_rejected: "#ef4444",   // red-500 (rejection)
  validator_assigned: "#2dd4bf",      // teal-400
  validation_in_progress: "#14b8a6",  // teal-500
  validation_completed: "#0d9488",    // teal-600
  validation_rejected: "#dc2626",     // red-600 (rejection)
  report_finalized: "#f43f5e",        // rose-500
  report_sent_to_client: "#e11d48",   // rose-600
}

const STAGE_DOT_COLORS: Record<string, string> = {
  site_created: "bg-slate-400", engineer_assigned: "bg-cyan-500", field_work_in_progress: "bg-orange-500",
  data_uploaded: "bg-orange-600", field_work_completed: "bg-orange-400", ta_review_pending: "bg-violet-400",
  ta_review_completed: "bg-violet-500", ta_rejected: "bg-violet-600", mis_report_assign_pending: "bg-amber-400",
  report_maker_assigned: "bg-amber-500", report_in_progress: "bg-amber-600", report_completed: "bg-amber-700",
  report_maker_rejected: "bg-red-500", validator_assigned: "bg-teal-400", validation_in_progress: "bg-teal-500",
  validation_completed: "bg-teal-600", validation_rejected: "bg-red-600",
  report_finalized: "bg-rose-500", report_sent_to_client: "bg-rose-600",
}

// INC sites run their own workflow, so they get their own palette. Kept in the
// blue/indigo family so an INC pin is instantly distinguishable from a survey pin.
const INC_STAGE_COLORS: Record<string, string> = {
  site_created: "#93c5fd",              // blue-300
  team_assigned: "#60a5fa",             // blue-400
  material_planned: "#3b82f6",          // blue-500
  material_requested: "#2563eb",        // blue-600
  material_issued: "#1d4ed8",           // blue-700
  material_delivered: "#1e40af",        // blue-800
  work_started: "#818cf8",              // indigo-400
  work_in_progress: "#6366f1",          // indigo-500
  visit_completed: "#4f46e5",           // indigo-600
  measurement_book_pending: "#a855f7",  // purple-500
  measurement_book_approved: "#9333ea", // purple-600
  boq_pending: "#c026d3",               // fuchsia-600
  boq_finalized: "#a21caf",             // fuchsia-700
  billing_pending: "#0891b2",           // cyan-600
  closed: "#155e75",                    // cyan-800
  cancelled: "#dc2626",                 // red-600
}
const INC_DEFAULT_COLOR = "#2563eb"

const PRIORITY_COLORS: Record<string, string> = {
  critical: "bg-red-600", high: "bg-orange-500", medium: "bg-yellow-500", low: "bg-green-500",
}

const WORKFLOW_STATUS_OPTIONS: SelectOption[] = [
  { id: 1, label: "Site Created", value: "site_created" },
  { id: 2, label: "Engineer Assigned", value: "engineer_assigned" },
  { id: 3, label: "Field Work In Progress", value: "field_work_in_progress" },
  { id: 4, label: "Field Work Completed", value: "field_work_completed" },
  { id: 5, label: "Data Uploaded", value: "data_uploaded" },
  { id: 6, label: "TA Review Pending", value: "ta_review_pending" },
  { id: 7, label: "TA Review Completed", value: "ta_review_completed" },
  { id: 8, label: "MIS Report Assign Pending", value: "mis_report_assign_pending" },
  { id: 9, label: "Report Maker Assigned", value: "report_maker_assigned" },
  { id: 10, label: "Report In Progress", value: "report_in_progress" },
  { id: 11, label: "Report Completed", value: "report_completed" },
  { id: 12, label: "Validator Assigned", value: "validator_assigned" },
  { id: 13, label: "Validation Completed", value: "validation_completed" },
  { id: 14, label: "Report Finalized", value: "report_finalized" },
  { id: 15, label: "Report Sent To Client", value: "report_sent_to_client" },
]

// ── helpers ─────────────────────────────────────────────────────────────────

const toNum = (v: unknown): number | null => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const getSiteCoords = (site: Site): { lat: number; lng: number } | null => {
  const lat = toNum(site.site_latitude)
  const lng = toNum(site.site_longitude)
  if (lat === null || lng === null) return null
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
  if (lat === 0 && lng === 0) return null
  return { lat, lng }
}

const haversineKm = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const fmtStage = (s?: string) => (s || "").replace(/_/g, " ") || "—"

// Opens site at EXACT coordinates in Google Maps — no label, so no place redirect
const siteGoogleMapsUrl = (lat: number, lng: number) =>
  `https://www.google.com/maps?q=${lat},${lng}&z=17`

// Opens all sites in Google Maps using coordinate-only waypoints (no labels = no place confusion)
const allSitesGoogleMapsUrl = (sites: Site[]): string | null => {
  const pts = sites.map(getSiteCoords).filter(Boolean) as { lat: number; lng: number }[]
  if (pts.length === 0) return null
  if (pts.length === 1) return `https://www.google.com/maps?q=${pts[0].lat},${pts[0].lng}`
  // /dir/ with raw lat,lng stops — Google Maps pins each stop at the exact coordinate
  return `https://www.google.com/maps/dir/${pts.slice(0, 25).map(p => `${p.lat},${p.lng}`).join("/")}`
}

// Build popup HTML for a site (shown inside Leaflet on marker click)
const buildPopupHtml = (site: Site, lat: number, lng: number, assignMode: "assign" | "reassign" | null) => {
  const gmUrl = siteGoogleMapsUrl(lat, lng)
  const eng = site.site_engineer
    ? `${site.site_engineer.first_name} ${site.site_engineer.last_name}${site.site_engineer.username ? ` (${site.site_engineer.username})` : ""}`.trim()
    : null
  const activityName = site.activity?.activity_name || null
  const inc = isIncSite(site)
  const typeBadge = `<span style="display:inline-block;font-size:9px;font-weight:700;letter-spacing:.4px;
      padding:1px 6px;border-radius:9px;margin-left:6px;vertical-align:middle;
      background:${inc ? "#dbeafe" : "#dcfce7"};color:${inc ? "#1d4ed8" : "#15803d"}">${inc ? "INC SITE" : "SURVEY SITE"}</span>`
  return `
<div style="font-family:system-ui,sans-serif;min-width:220px;max-width:280px;line-height:1.65">
  <div style="font-size:14px;font-weight:700;color:#1e3a5f;margin-bottom:4px">${site.site_id}${typeBadge}</div>
  ${site.site_name ? `<div style="font-size:12px;color:#374151;margin-bottom:6px">${site.site_name}</div>` : ""}
  <table style="font-size:11px;width:100%;border-collapse:collapse">
    <tr><td style="color:#6b7280;padding:1px 8px 1px 0;white-space:nowrap;vertical-align:top">Site Type</td>
        <td style="font-weight:600">${inc ? "INC Site" : "Survey Site"}</td></tr>
    <tr><td style="color:#6b7280;padding:1px 8px 1px 0;white-space:nowrap;vertical-align:top">Stage</td>
        <td style="font-weight:600">${fmtStage(site.survey_workflow_status)}${isOnHold(site)
          ? ` <span style="display:inline-block;font-size:9px;font-weight:700;padding:0 6px;border-radius:9px;background:${HOLD_COLOR};color:#fff">ON HOLD</span>`
          : ""}</td></tr>
    <tr><td style="color:#6b7280;padding:1px 8px 1px 0;white-space:nowrap;vertical-align:top">Created On</td>
        <td>${fmtCreatedDate(site.created_at)}</td></tr>
    ${inc && site.work_type ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">Work Type</td><td style="font-weight:600">${site.work_type}</td></tr>` : ""}
    ${inc && site.team_type ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">Team</td><td style="text-transform:capitalize">${site.team_type}</td></tr>` : ""}
    ${site.site_priority ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">Priority</td><td style="font-weight:600;text-transform:capitalize">${site.site_priority}</td></tr>` : ""}
    ${site.project ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">Project</td><td>${site.project.project_name}</td></tr>` : ""}
    ${activityName ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">Activity</td><td>${activityName}</td></tr>` : ""}
    ${eng ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">${inc ? "Supervisor" : "Engineer"}</td><td>${eng}</td></tr>` : ""}
    ${site.technician_name ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">Technician</td><td>${site.technician_name}</td></tr>` : ""}
    ${site.fse_name ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">FSE</td><td>${site.fse_name}</td></tr>` : ""}
    ${site.district?.name ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">District</td><td>${site.district.name}</td></tr>` : ""}
    <tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">Site Coordinates</td>
        <td style="font-family:monospace;font-size:10px">${lat.toFixed(6)}, ${lng.toFixed(6)}</td></tr>
  </table>
  <div style="margin-top:8px;display:flex;flex-wrap:wrap;gap:6px">
    <a href="${gmUrl}" target="_blank" rel="noopener noreferrer"
       style="display:inline-flex;align-items:center;gap:4px;font-size:11px;color:#16a34a;
              text-decoration:none;border:1px solid #16a34a;border-radius:4px;padding:3px 8px">
      📍 Open in Google Maps
    </a>
    ${assignMode
      ? `<button type="button" data-assign-site="${site.id}"
           style="display:inline-flex;align-items:center;gap:4px;font-size:11px;color:#1d4ed8;cursor:pointer;
                  background:#eff6ff;border:1px solid #1d4ed8;border-radius:4px;padding:3px 8px;font-family:inherit">
           👷 ${assignMode === "reassign" ? "Reassign" : "Assign"} Engineer
         </button>`
      : ""}
  </div>
</div>`
}

const fmtTime = (t?: string | null) => {
  if (!t) return "—"
  try { return new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) } catch { return t }
}

const buildReportingPopupHtml = (record: AttendanceRecord) => {
  const u = record.user
  const name = u ? `${u.first_name} ${u.last_name}`.trim() : `User #${record.user_id}`
  const lat = toNum(u?.reporting_latitude ?? record.reporting_latitude)
  const lng = toNum(u?.reporting_longitude ?? record.reporting_longitude)
  return `<div style="font-family:system-ui,sans-serif;min-width:210px;max-width:270px;line-height:1.7">
  <div style="font-size:13px;font-weight:700;color:#1d4ed8;margin-bottom:3px">🏠 Reporting Location</div>
  <div style="font-size:12px;color:#374151;font-weight:600;margin-bottom:6px">${name}</div>
  <table style="font-size:11px;width:100%;border-collapse:collapse">
    ${u?.employee_code ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;white-space:nowrap">Employee ID</td><td style="font-weight:600">${u.employee_code}</td></tr>` : ""}
    ${u?.district?.name ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0">District</td><td>${u.district.name}</td></tr>` : ""}
    <tr><td style="color:#6b7280;padding:1px 8px 1px 0">GPS</td><td style="font-family:monospace;font-size:10px">${lat?.toFixed(5)}, ${lng?.toFixed(5)}</td></tr>
    ${record.work_status ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0">Work Status</td><td style="text-transform:capitalize">${record.work_status.replace(/_/g," ")}</td></tr>` : ""}
  </table>
  <a href="https://www.google.com/maps?q=${lat},${lng}&z=17" target="_blank" rel="noopener noreferrer"
     style="margin-top:7px;display:inline-flex;align-items:center;gap:4px;font-size:11px;color:#1d4ed8;text-decoration:none;border:1px solid #1d4ed8;border-radius:4px;padding:3px 8px">
    📍 Open in Google Maps
  </a>
</div>`
}

const buildCheckInPopupHtml = (record: AttendanceRecord) => {
  const u = record.user
  const name = u ? `${u.first_name} ${u.last_name}`.trim() : `User #${record.user_id}`
  const lat = toNum(record.check_in_latitude)
  const lng = toNum(record.check_in_longitude)
  return `<div style="font-family:system-ui,sans-serif;min-width:210px;max-width:270px;line-height:1.7">
  <div style="font-size:13px;font-weight:700;color:#15803d;margin-bottom:3px">✅ Today's Check-In</div>
  <div style="font-size:12px;color:#374151;font-weight:600;margin-bottom:6px">${name}</div>
  <table style="font-size:11px;width:100%;border-collapse:collapse">
    <tr><td style="color:#6b7280;padding:1px 8px 1px 0;white-space:nowrap">Check-in Time</td><td style="font-weight:600">${fmtTime(record.check_in_time)}</td></tr>
    ${record.check_in_address ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">Address</td><td>${record.check_in_address}</td></tr>` : ""}
    ${u?.district?.name ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0">District</td><td>${u.district.name}</td></tr>` : ""}
    <tr><td style="color:#6b7280;padding:1px 8px 1px 0">GPS</td><td style="font-family:monospace;font-size:10px">${lat?.toFixed(5)}, ${lng?.toFixed(5)}</td></tr>
    ${record.work_status ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0">Status</td><td style="text-transform:capitalize">${record.work_status.replace(/_/g," ")}</td></tr>` : ""}
    ${u?.employee_code ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0">Employee ID</td><td>${u.employee_code}</td></tr>` : ""}
  </table>
  <a href="https://www.google.com/maps?q=${lat},${lng}&z=17" target="_blank" rel="noopener noreferrer"
     style="margin-top:7px;display:inline-flex;align-items:center;gap:4px;font-size:11px;color:#15803d;text-decoration:none;border:1px solid #15803d;border-radius:4px;padding:3px 8px">
    📍 Open in Google Maps
  </a>
</div>`
}

const buildCheckOutPopupHtml = (record: AttendanceRecord) => {
  const u = record.user
  const name = u ? `${u.first_name} ${u.last_name}`.trim() : `User #${record.user_id}`
  const lat = toNum(record.check_out_latitude)
  const lng = toNum(record.check_out_longitude)
  return `<div style="font-family:system-ui,sans-serif;min-width:210px;max-width:270px;line-height:1.7">
  <div style="font-size:13px;font-weight:700;color:#c2410c;margin-bottom:3px">🏁 Today's Check-Out</div>
  <div style="font-size:12px;color:#374151;font-weight:600;margin-bottom:6px">${name}</div>
  <table style="font-size:11px;width:100%;border-collapse:collapse">
    <tr><td style="color:#6b7280;padding:1px 8px 1px 0;white-space:nowrap">Check-out Time</td><td style="font-weight:600">${fmtTime(record.check_out_time)}</td></tr>
    ${record.check_out_address ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0;vertical-align:top">Address</td><td>${record.check_out_address}</td></tr>` : ""}
    <tr><td style="color:#6b7280;padding:1px 8px 1px 0">GPS</td><td style="font-family:monospace;font-size:10px">${lat?.toFixed(5)}, ${lng?.toFixed(5)}</td></tr>
    ${record.work_status ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0">Status</td><td style="text-transform:capitalize">${record.work_status.replace(/_/g," ")}</td></tr>` : ""}
    ${u?.employee_code ? `<tr><td style="color:#6b7280;padding:1px 8px 1px 0">Employee ID</td><td>${u.employee_code}</td></tr>` : ""}
  </table>
  <a href="https://www.google.com/maps?q=${lat},${lng}&z=17" target="_blank" rel="noopener noreferrer"
     style="margin-top:7px;display:inline-flex;align-items:center;gap:4px;font-size:11px;color:#c2410c;text-decoration:none;border:1px solid #c2410c;border-radius:4px;padding:3px 8px">
    📍 Open in Google Maps
  </a>
</div>`
}

// ── Leaflet loader ───────────────────────────────────────────────────────────

let _lfLoaded = false
let _lfLoading = false
const _lfCbs: Array<() => void> = []

function loadLeaflet(cb: () => void) {
  if (_lfLoaded) { cb(); return }
  _lfCbs.push(cb)
  if (_lfLoading) return
  _lfLoading = true
  const link = document.createElement("link")
  link.rel = "stylesheet"
  link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
  document.head.appendChild(link)
  const script = document.createElement("script")
  script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"
  script.onload = () => { _lfLoaded = true; _lfCbs.splice(0).forEach(fn => fn()) }
  document.head.appendChild(script)
}

// ── constants ────────────────────────────────────────────────────────────────

const INPUT_CLS = "w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
const SELECT_CLS = "w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
const LABEL_CLS = "block text-sm font-medium text-gray-700 mb-2"

const filterTabs = [
  { id: "basic", label: "Basic Info", icon: FiSearch },
  { id: "location", label: "Location", icon: FiMapPin },
  { id: "dates", label: "Dates", icon: FiCalendar },
  { id: "assignments", label: "Assignments", icon: FiUser },
  { id: "status", label: "Status", icon: FiSettings },
]

// ── component ────────────────────────────────────────────────────────────────

type ViewMode = "list" | "map"

const MapViewPage: React.FC = () => {
  const { user, hasPermission } = useAuth()
  const isSiteEngineer = (user?.role?.name || "").toLowerCase() === "site engineer"
  // Same gate as the Project Sites table — assigning from the map is the same
  // action, just reached from a pin instead of a row.
  const canAssignEngineer = hasPermission("Site", "assign") || hasPermission("Site", "re_assign")

  const [sites, setSites] = useState<Site[]>([])
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [filterTab, setFilterTab] = useState("basic")
  const [viewMode, setViewMode] = useState<ViewMode>("map")
  const [leafletReady, setLeafletReady] = useState(false)

  const [filters, setFilters] = useState<Filters>({})
  const [availableProjects, setAvailableProjects] = useState<{ value: number; label: string }[]>([])
  const [selectedProjects, setSelectedProjects] = useState<{ value: number | string; label: string }[]>([])
  const [selectedActivity, setSelectedActivity] = useState<SelectOption | null>(null)
  const [selectedActivityStatus, setSelectedActivityStatus] = useState<SelectOption | null>(null)
  const [selectedStateCircle, setSelectedStateCircle] = useState<SelectOption | null>(null)
  const [selectedDistrict, setSelectedDistrict] = useState<SelectOption | null>(null)
  const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState<SelectOption | null>(null)
  const [availableSiteEngineers, setAvailableSiteEngineers] = useState<{ value: number; label: string }[]>([])
  const [selectedSiteEngineer, setSelectedSiteEngineer] = useState<{ value: number; label: string } | null>(null)

  // The site whose engineer is being (re)assigned from a pin's popup.
  const [assignSite, setAssignSite] = useState<Site | null>(null)
  const [assigning, setAssigning] = useState(false)
  // Closest engineers to the site being assigned (site <-> engineer reporting location).
  const [engineerSuggestions, setEngineerSuggestions] = useState<EngineerSuggestion[]>([])
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)

  // Location for optional radius filter
  const [currentLat, setCurrentLat] = useState("")
  const [currentLng, setCurrentLng] = useState("")
  const [locationAddress, setLocationAddress] = useState("")
  const [locationLoading, setLocationLoading] = useState(false)
  const [locationError, setLocationError] = useState("")
  const [useRadius, setUseRadius] = useState(false)
  const [radiusKm, setRadiusKm] = useState(50)
  const [reportingLocationLabel, setReportingLocationLabel] = useState("")

  const [onlyAssigned, setOnlyAssigned] = useState(false)
  const [onlyWorkable, setOnlyWorkable] = useState(false)
  // INC sites are plotted alongside survey sites; on by default so the map shows all
  // field work in one place. Toggling this re-loads, since the API decides what to send.
  const [includeIncSites, setIncludeIncSites] = useState(true)

  // Attendance layer state
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([])
  const [attendanceLoading, setAttendanceLoading] = useState(false)
  const [attendanceLoaded, setAttendanceLoaded] = useState(false)
  const [showSiteLayer, setShowSiteLayer] = useState(true)
  const [showReportingLayer, setShowReportingLayer] = useState(false)
  const [showCheckInLayer, setShowCheckInLayer] = useState(false)
  const [showCheckOutLayer, setShowCheckOutLayer] = useState(false)

  // Leaflet refs
  const mapDivRef = useRef<HTMLDivElement>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const leafletMapRef = useRef<any>(null)
  const siteLayerRef = useRef<any>(null)
  const reportingLayerRef = useRef<any>(null)
  const checkInLayerRef = useRef<any>(null)
  const checkOutLayerRef = useRef<any>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const toggleFullscreen = () => {
    const el = mapContainerRef.current
    if (!el) return
    if (!document.fullscreenElement) {
      el.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {})
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {})
    }
  }

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener("fullscreenchange", handler)
    return () => document.removeEventListener("fullscreenchange", handler)
  }, [])

  useEffect(() => { loadLeaflet(() => setLeafletReady(true)) }, [])

  // When the assign dialog opens, ask the API for the site's assignable engineers
  // ordered by distance from the site to each engineer's reporting location.
  useEffect(() => {
    setEngineerSuggestions([])
    if (!assignSite) return
    const coords = getSiteCoords(assignSite)
    if (!coords) return
    let cancelled = false
    setSuggestionsLoading(true)
    const params = new URLSearchParams({
      page: "1",
      limit: "5",
      project_id: String(assignSite.project_id),
      src: "all",
      near_latitude: String(coords.lat),
      near_longitude: String(coords.lng),
    })
    SITE_ENGINEER_ASSIGNABLE_ROLE_IDS.forEach(id => params.append("role_id", String(id)))
    if (user?.id) params.append("exclude_user_id", String(user.id))
    api.get(`/survey/sites/assignment/users?${params}`)
      .then(res => {
        if (cancelled) return
        const users = res.data?.data?.users || []
        setEngineerSuggestions(
          users.map((u: any) => ({
            id: u.id,
            label: `${u.first_name} ${u.last_name} (${u.username}) - ${u?.employee_code || "N/A"}`,
            distance_km: typeof u.distance_km === "number" ? u.distance_km : null,
          })),
        )
      })
      .catch(() => { if (!cancelled) setEngineerSuggestions([]) })
      .finally(() => { if (!cancelled) setSuggestionsLoading(false) })
    return () => { cancelled = true }
  }, [assignSite, user?.id])

  useEffect(() => {
    const params: Record<string, any> = { page: 1, limit: 500, status: "ACTIVE" }
    if (user?.role?.level !== 1) params.assigned_user_ids = user?.id
    api.get("/projects/list", { params }).then(res => {
      if (res.data.success) {
        setAvailableProjects((res.data.data.projects || []).map((p: any) => ({ value: p.id, label: p.project_name })))
      }
    }).catch(() => {})
  }, [user?.id, user?.role?.level])

  useEffect(() => {
    // `assigned_only` narrows the dropdown to engineers sharing a project with
    // the caller, so it cannot offer someone whose sites can never appear on
    // this map. Super admins keep the full roster.
    api.get("/survey/users/role/7", { params: { assigned_only: true } }).then(res => {
      if (res.data.success) {
        setAvailableSiteEngineers(
          (res.data.data.users || []).map((u: any) => ({
            value: u.id,
            label: `${u.first_name} ${u.last_name}`.trim(),
          }))
        )
      }
    }).catch(() => {})
  }, [])

  // For site engineers: auto-set reporting location as distance anchor
  useEffect(() => {
    if (!isSiteEngineer) return
    api.get("/auth/profile").then(res => {
      const u = res.data?.data?.user || res.data?.data || res.data?.user
      const lat = toNum(u?.reporting_latitude)
      const lng = toNum(u?.reporting_longitude)
      if (lat !== null && lng !== null && !(lat === 0 && lng === 0)) {
        setCurrentLat(String(lat))
        setCurrentLng(String(lng))
        setReportingLocationLabel(`Reporting location: ${lat.toFixed(5)}, ${lng.toFixed(5)}`)
      }
    }).catch(() => {})
  }, [isSiteEngineer])

  // ── computed ─────────────────────────────────────────────────────────────

  const anchor = useMemo(() => {
    const lat = toNum(currentLat); const lng = toNum(currentLng)
    return lat !== null && lng !== null ? { latitude: lat, longitude: lng } : null
  }, [currentLat, currentLng])

  const sitesWithGps = useMemo(() => sites.filter(s => getSiteCoords(s) !== null), [sites])
  const sitesNoGps = useMemo(() => sites.filter(s => getSiteCoords(s) === null), [sites])

  const filteredSites = useMemo(() => {
    let r = sitesWithGps.filter(s => s.status !== "cancelled")
    if (onlyAssigned) r = r.filter(s => !!s.site_engineer_userid || !!s.site_engineer)
    if (onlyWorkable) r = r.filter(s => ["engineer_assigned", "field_work_in_progress"].includes(s.survey_workflow_status))
    if (anchor && useRadius) {
      r = r.filter(s => {
        const c = getSiteCoords(s)
        return c !== null && haversineKm(anchor.latitude, anchor.longitude, c.lat, c.lng) <= radiusKm
      })
    }
    return r
  }, [sitesWithGps, anchor, useRadius, onlyAssigned, onlyWorkable, radiusKm])

  const googleMapsUrl = useMemo(() => allSitesGoogleMapsUrl(filteredSites), [filteredSites])
  const incSiteCount = useMemo(() => filteredSites.filter(isIncSite).length, [filteredSites])
  const surveySiteCount = filteredSites.length - incSiteCount

  // ── Site markers layer ───────────────────────────────────────────────────

  useEffect(() => {
    if (!loaded || !leafletReady || !mapDivRef.current) return
    if (viewMode !== "map") return

    const L = window.L
    if (!L) return

    // Initialize map + all 4 layer groups once
    if (!leafletMapRef.current) {
      const center: [number, number] = filteredSites.length > 0
        ? [getSiteCoords(filteredSites[0])!.lat, getSiteCoords(filteredSites[0])!.lng]
        : [20.5937, 78.9629]

      leafletMapRef.current = L.map(mapDivRef.current, { center, zoom: filteredSites.length > 0 ? 7 : 5 })
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(leafletMapRef.current)
      siteLayerRef.current = L.layerGroup().addTo(leafletMapRef.current)
      reportingLayerRef.current = L.layerGroup().addTo(leafletMapRef.current)
      checkInLayerRef.current = L.layerGroup().addTo(leafletMapRef.current)
      checkOutLayerRef.current = L.layerGroup().addTo(leafletMapRef.current)
    }

    siteLayerRef.current.clearLayers()

    if (showSiteLayer) {
      const bounds: [number, number][] = []

      filteredSites.forEach(site => {
        const coords = getSiteCoords(site)
        if (!coords) return
        const { lat, lng } = coords
        const inc = isIncSite(site)
        const color = isOnHold(site)
          ? HOLD_COLOR
          : inc
            ? INC_STAGE_COLORS[site.survey_workflow_status] || INC_DEFAULT_COLOR
            : STAGE_COLORS[site.survey_workflow_status] || "#64748b"

        // Survey pins carry a circle, INC pins a square — the shape reads at a glance
        // even for viewers who cannot tell the two colour families apart.
        const glyph = inc
          ? `<rect x="8" y="8" width="8" height="8" rx="1" fill="#fff"/>`
          : `<circle cx="12" cy="12" r="4.5" fill="#fff"/>`

        const svgIcon = L.divIcon({
          className: "",
          html: `<div style="display:flex;flex-direction:column;align-items:center;">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
              <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24C24 5.373 18.627 0 12 0z"
                fill="${color}" stroke="#fff" stroke-width="1.5"/>
              ${glyph}
            </svg>
            <span style="background:rgba(0,0,0,0.72);color:#fff;font-size:9px;padding:1px 4px;border-radius:3px;margin-top:1px;white-space:nowrap;max-width:90px;overflow:hidden;text-overflow:ellipsis;">${isOnHold(site) ? "HOLD · " : ""}${inc ? "INC · " : ""}${site.site_id}</span>
          </div>`,
          iconSize: [96, 52],
          iconAnchor: [48, 36],
          popupAnchor: [0, -36],
        })

        const assignMode = canAssignEngineer ? mapAssignMode(site) : null

        const marker = L.marker([lat, lng], { icon: svgIcon }).addTo(siteLayerRef.current)
        marker.bindPopup(buildPopupHtml(site, lat, lng, assignMode), { maxWidth: 300 })
        if (assignMode) {
          // The popup is raw HTML, so the button can only be wired once Leaflet
          // has put it in the DOM. `once` matches the element's lifetime: the
          // popup content is rebuilt on every open.
          marker.on("popupopen", (event: any) => {
            const button = event.popup.getElement()?.querySelector("[data-assign-site]")
            if (!button) return
            button.addEventListener(
              "click",
              () => {
                leafletMapRef.current?.closePopup()
                setAssignSite(site)
              },
              { once: true },
            )
          })
        }
        bounds.push([lat, lng])
      })

      if (bounds.length > 0) {
        leafletMapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })
      }
    }

    const t = setTimeout(() => leafletMapRef.current?.invalidateSize(), 150)
    return () => clearTimeout(t)
  }, [loaded, leafletReady, filteredSites, viewMode, showSiteLayer, canAssignEngineer])

  // ── Reporting location layer ─────────────────────────────────────────────

  useEffect(() => {
    if (!loaded || !leafletReady || !leafletMapRef.current || !reportingLayerRef.current) return
    if (viewMode !== "map") return
    const L = window.L
    if (!L) return

    reportingLayerRef.current.clearLayers()
    if (!showReportingLayer) return

    attendanceData.forEach(record => {
      const lat = toNum(record.user?.reporting_latitude ?? record.reporting_latitude)
      const lng = toNum(record.user?.reporting_longitude ?? record.reporting_longitude)
      if (lat === null || lng === null || (lat === 0 && lng === 0)) return

      const userName = [record.user?.first_name, record.user?.last_name].filter(Boolean).join(" ") || "Unknown"
      const icon = L.divIcon({
        className: "",
        html: `<div style="display:flex;flex-direction:column;align-items:center;">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
            <circle cx="16" cy="16" r="14" fill="#9333ea" stroke="#fff" stroke-width="2.5"/>
            <path d="M16 7L8 14h3v9h4v-5h2v5h4v-9h3L16 7z" fill="#fff"/>
          </svg>
          <span style="background:rgba(147,51,234,0.85);color:#fff;font-size:9px;padding:1px 4px;border-radius:3px;margin-top:1px;white-space:nowrap;max-width:90px;overflow:hidden;text-overflow:ellipsis;">${userName}</span>
        </div>`,
        iconSize: [96, 48],
        iconAnchor: [48, 32],
        popupAnchor: [0, -32],
      })

      const marker = L.marker([lat, lng], { icon }).addTo(reportingLayerRef.current)
      marker.bindPopup(buildReportingPopupHtml(record), { maxWidth: 290 })
    })
  }, [loaded, leafletReady, viewMode, showReportingLayer, attendanceData])

  // ── Check-in location layer ──────────────────────────────────────────────

  useEffect(() => {
    if (!loaded || !leafletReady || !leafletMapRef.current || !checkInLayerRef.current) return
    if (viewMode !== "map") return
    const L = window.L
    if (!L) return

    checkInLayerRef.current.clearLayers()
    if (!showCheckInLayer) return

    attendanceData.forEach(record => {
      const lat = toNum(record.check_in_latitude)
      const lng = toNum(record.check_in_longitude)
      if (lat === null || lng === null || (lat === 0 && lng === 0)) return

      const checkInUserName = [record.user?.first_name, record.user?.last_name].filter(Boolean).join(" ") || "Unknown"
      const icon = L.divIcon({
        className: "",
        html: `<div style="display:flex;flex-direction:column;align-items:center;">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="22" height="34">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24C24 5.373 18.627 0 12 0z"
              fill="#059669" stroke="#fff" stroke-width="1.5"/>
            <path d="M7.5 12.5l3 3 6-6" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
          <span style="background:rgba(5,150,105,0.85);color:#fff;font-size:9px;padding:1px 4px;border-radius:3px;margin-top:1px;white-space:nowrap;max-width:90px;overflow:hidden;text-overflow:ellipsis;">${checkInUserName}</span>
        </div>`,
        iconSize: [96, 50],
        iconAnchor: [48, 34],
        popupAnchor: [0, -34],
      })

      const marker = L.marker([lat, lng], { icon }).addTo(checkInLayerRef.current)
      marker.bindPopup(buildCheckInPopupHtml(record), { maxWidth: 290 })
    })
  }, [loaded, leafletReady, viewMode, showCheckInLayer, attendanceData])

  // ── Check-out location layer ─────────────────────────────────────────────

  useEffect(() => {
    if (!loaded || !leafletReady || !leafletMapRef.current || !checkOutLayerRef.current) return
    if (viewMode !== "map") return
    const L = window.L
    if (!L) return

    checkOutLayerRef.current.clearLayers()
    if (!showCheckOutLayer) return

    attendanceData.forEach(record => {
      const lat = toNum(record.check_out_latitude)
      const lng = toNum(record.check_out_longitude)
      if (lat === null || lng === null || (lat === 0 && lng === 0)) return

      const checkOutUserName = [record.user?.first_name, record.user?.last_name].filter(Boolean).join(" ") || "Unknown"
      const icon = L.divIcon({
        className: "",
        html: `<div style="display:flex;flex-direction:column;align-items:center;">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="22" height="34">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24C24 5.373 18.627 0 12 0z"
              fill="#e11d48" stroke="#fff" stroke-width="1.5"/>
            <path d="M12 8v7M9 13l3 3 3-3" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span style="background:rgba(225,29,72,0.85);color:#fff;font-size:9px;padding:1px 4px;border-radius:3px;margin-top:1px;white-space:nowrap;max-width:90px;overflow:hidden;text-overflow:ellipsis;">${checkOutUserName}</span>
        </div>`,
        iconSize: [96, 50],
        iconAnchor: [48, 34],
        popupAnchor: [0, -34],
      })

      const marker = L.marker([lat, lng], { icon }).addTo(checkOutLayerRef.current)
      marker.bindPopup(buildCheckOutPopupHtml(record), { maxWidth: 290 })
    })
  }, [loaded, leafletReady, viewMode, showCheckOutLayer, attendanceData])

  // Destroy Leaflet when component unmounts
  useEffect(() => () => {
    if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current = null }
    siteLayerRef.current = null
    reportingLayerRef.current = null
    checkInLayerRef.current = null
    checkOutLayerRef.current = null
  }, [])

  // ── handlers ─────────────────────────────────────────────────────────────

  const fetchAttendance = async () => {
    setAttendanceLoading(true)
    try {
      const today = new Date().toISOString().split("T")[0]
      const res = await api.get("/attendance/all", {
        params: { attendance_date_from: today, attendance_date_to: today, limit: 500, page: 1, scope: "project" },
      })
      if (res.data.success) {
        setAttendanceData(res.data.data.attendances || [])
        setAttendanceLoaded(true)
      }
    } catch (err) {
      console.error("Error fetching attendance:", err)
    } finally {
      setAttendanceLoading(false)
    }
  }

  const handleDetectLocation = () => {
    if (!navigator.geolocation) { setLocationError("Geolocation not supported."); return }
    setLocationLoading(true); setLocationError(""); setLocationAddress("")
    navigator.geolocation.getCurrentPosition(async pos => {
      const lat = pos.coords.latitude; const lng = pos.coords.longitude
      setCurrentLat(String(lat)); setCurrentLng(String(lng))
      try {
        const res = await api.post("/maps/reverse-geocode", { lat, lng })
        setLocationAddress(res.data?.data?.formatted_address || res.data?.data?.address || "")
      } catch { setLocationAddress(`${lat.toFixed(5)}, ${lng.toFixed(5)}`) }
      setLocationLoading(false)
    }, () => {
      setLocationError("Could not get location — please allow access and retry.")
      setLocationLoading(false)
    }, { enableHighAccuracy: true, timeout: 10000 })
  }

  const handleInputChange = (field: keyof Filters, value: string) =>
    setFilters(prev => ({ ...prev, [field]: value || undefined }))

  const handleStageGroupChange = (group: string) => {
    setFilters(prev => ({ ...prev, stage_group: group || undefined, survey_workflow_status: undefined, workflow_status_list: group ? STAGE_GROUP_MAP[group] : undefined }))
    setSelectedWorkflowStatus(null)
  }

  const handleLoad = async () => {
    const dateError = firstDateRangeError([
      [filters.created_from, filters.created_to, "Created Date"],
      [filters.pr_date_from, filters.pr_date_to, "PR Date"],
      [filters.po_date_from, filters.po_date_to, "PO Date"],
    ])
    if (dateError) {
      toast.error(dateError)
      return
    }
    setLoading(true)
    try {
      const params: Record<string, unknown> = { page: 1, limit: 2000 }
      const f = filters
      if (f.site_id) params.site_id = f.site_id
      if (f.site_id_list?.trim()) params.site_id_list = f.site_id_list.trim().split(/[\s,]+/).filter(Boolean).join(",")
      if (f.site_name) params.site_name = f.site_name
      if (f.site_address) params.site_address = f.site_address
      if (f.activity_id) params.activity_id = f.activity_id
      if (f.reference_number) params.reference_number = f.reference_number
      if (selectedProjects.length > 0) params.project_id = selectedProjects.map(p => p.value).join(",")
      else if (f.project_id) params.project_id = f.project_id
      if (f.state_circle_id) params.state_circle_id = f.state_circle_id
      if (f.district_id) params.district_id = f.district_id
      if (f.pincode) params.pincode = f.pincode
      if (f.opco_name) params.opco_name = f.opco_name
      if (f.opco_id) params.opco_id = f.opco_id
      if (f.created_from) params.created_from = f.created_from
      if (f.created_to) params.created_to = f.created_to
      if (f.pr_date_from) params.pr_date_from = f.pr_date_from
      if (f.pr_date_to) params.pr_date_to = f.pr_date_to
      if (f.po_date_from) params.po_date_from = f.po_date_from
      if (f.po_date_to) params.po_date_to = f.po_date_to
      if (f.pr_number) params.pr_number = f.pr_number
      if (f.po_number) params.po_number = f.po_number
      if (f.is_po_no_shared_at_allocation) params.is_po_no_shared_at_allocation = f.is_po_no_shared_at_allocation
      if (f.pending_assignment) params.pending_assignment = f.pending_assignment
      if (f.technician_name) params.technician_name = f.technician_name
      if (f.technician_number) params.technician_number = f.technician_number
      if (f.fse_name) params.fse_name = f.fse_name
      if (f.fse_number) params.fse_number = f.fse_number
      if (f.aom_name) params.aom_name = f.aom_name
      if (f.aom_number) params.aom_number = f.aom_number
      if (f.site_priority) params.site_priority = f.site_priority
      if (f.workflow_status_list) params.workflow_status_list = f.workflow_status_list
      else if (f.survey_workflow_status) params.survey_workflow_status = f.survey_workflow_status
      if (selectedSiteEngineer) params.site_engineer_userid = selectedSiteEngineer.value
      if (includeIncSites) params.include_inc_sites = true

      const res = await api.get("/survey/sites/all", { params })
      if (res.data.success) {
        // Destroy existing map so it reinitialises with fresh data
        if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current = null }
        setSites(res.data.data.sites || [])
        setLoaded(true)
        setViewMode("map")
      }
    } catch (err) { console.error("Error fetching sites:", err) }
    finally { setLoading(false) }
  }

  /**
   * Assign or reassign the engineer on one site.
   *
   * Two endpoints because the backend draws the same line the UI does: the
   * first assignment moves the site to `engineer_assigned`, a later change is
   * an assignment update and leaves the stage where it is.
   */
  const handleAssignEngineer = async (option: { id?: number; value: number; label: string }) => {
    if (!assignSite) return
    const engineerId = Number(option.value ?? option.id)
    if (!Number.isFinite(engineerId)) return

    setAssigning(true)
    try {
      if (assignSite.site_engineer_userid) {
        await api.put("/survey/sites/update-assignment", {
          site_ids: [assignSite.id],
          site_engineer_userid: engineerId,
        })
      } else {
        await api.post(`/survey/sites/${assignSite.id}/assign-engineer`, {
          user: { id: engineerId, value: engineerId },
        })
      }

      // Patch the pin in place rather than refetching every site on the map:
      // a reload would discard the current view and re-run the whole query.
      const [firstName, ...restName] = String(option.label || "").split(" ")
      setSites(prev =>
        prev.map(s =>
          s.id === assignSite.id && s.site_type === assignSite.site_type
            ? {
              ...s,
              site_engineer_userid: engineerId,
              site_engineer: {
                id: engineerId,
                first_name: firstName || "",
                last_name: restName.join(" "),
              },
              survey_workflow_status:
                s.survey_workflow_status === "site_created"
                  ? "engineer_assigned"
                  : s.survey_workflow_status,
            }
            : s,
        ),
      )
      setAssignSite(null)
    } catch (err) {
      console.error("Error assigning engineer:", err)
    } finally {
      setAssigning(false)
    }
  }

  const handleReset = () => {
    setFilters({}); setSelectedProjects([]); setSelectedActivity(null); setSelectedActivityStatus(null)
    setSelectedStateCircle(null); setSelectedDistrict(null); setSelectedWorkflowStatus(null)
    setSelectedSiteEngineer(null)
    setCurrentLat(""); setCurrentLng(""); setLocationAddress(""); setLocationError("")
    setUseRadius(false); setRadiusKm(50); setOnlyAssigned(false); setOnlyWorkable(false)
    setIncludeIncSites(true)
    setSites([]); setLoaded(false); setFilterTab("basic"); setViewMode("map")
    setAttendanceData([]); setAttendanceLoaded(false); setAttendanceLoading(false)
    setShowSiteLayer(true); setShowReportingLayer(false); setShowCheckInLayer(false); setShowCheckOutLayer(false)
    if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current = null }
    siteLayerRef.current = null; reportingLayerRef.current = null
    checkInLayerRef.current = null; checkOutLayerRef.current = null
  }

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Map View</h1>
          <p className="mt-0.5 text-sm text-gray-500">Apply filters, load sites — click any pin to see site details.</p>
        </div>
        <button type="button" onClick={handleReset}
          className="inline-flex items-center gap-2 rounded bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200">
          <FiRefreshCw className="h-4 w-4" /> Reset
        </button>
      </div>

      {/* Filter panel */}
      <div className="border-b border-gray-200 bg-white shadow-sm">
        {/* Filter tabs */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {filterTabs.map(tab => (
            <button key={tab.id} type="button" onClick={() => setFilterTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${filterTab === tab.id ? "border-blue-500 text-blue-600 bg-white" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        <div className="px-6 py-5">
          {/* Basic */}
          {filterTab === "basic" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div><label className={LABEL_CLS}>Site ID</label><input type="text" className={INPUT_CLS} placeholder="Site ID" value={filters.site_id || ""} onChange={e => handleInputChange("site_id", e.target.value)} /></div>
              <div><label className={LABEL_CLS}>Site Name</label><input type="text" className={INPUT_CLS} placeholder="Site Name" value={filters.site_name || ""} onChange={e => handleInputChange("site_name", e.target.value)} /></div>
              <div><label className={LABEL_CLS}>Site Address</label><input type="text" className={INPUT_CLS} placeholder="Address" value={filters.site_address || ""} onChange={e => handleInputChange("site_address", e.target.value)} /></div>
              <div><label className={LABEL_CLS}>Reference Number</label><input type="text" className={INPUT_CLS} placeholder="Reference" value={filters.reference_number || ""} onChange={e => handleInputChange("reference_number", e.target.value)} /></div>
            </div>
          )}

          {/* Location */}
          {filterTab === "location" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div><label className={LABEL_CLS}>State Circles</label>
                <SelectWithSearch placeholder="Select State Circle" value={selectedStateCircle}
                  onChange={o => { setSelectedStateCircle(o); setSelectedDistrict(null); setFilters(p => ({ ...p, state_circle_id: o ? o.value.toString() : undefined, district_id: undefined })) }}
                  apiEndpoint="/state-circle/list" dataKey="states" labelKey="state_name" valueKey="id" searchParam="search" className="w-full" />
              </div>
              <div><label className={LABEL_CLS}>Districts</label>
                <SelectWithSearch placeholder="Select District" value={selectedDistrict}
                  onChange={o => { setSelectedDistrict(o); setFilters(p => ({ ...p, district_id: o ? o.value.toString() : undefined })) }}
                  apiEndpoint="/state-circle" dataKey="districts" labelKey="name" valueKey="id" searchParam="search"
                  dependencyId={selectedStateCircle ? Number(selectedStateCircle.value) : null} className="w-full" />
              </div>
              <div><label className={LABEL_CLS}>Pincode</label><input type="number" className={INPUT_CLS} placeholder="Pincode" value={filters.pincode || ""} onChange={e => handleInputChange("pincode", e.target.value)} /></div>
              <div><label className={LABEL_CLS}>OPCO Name</label><input type="text" className={INPUT_CLS} placeholder="OPCO Name" value={filters.opco_name || ""} onChange={e => handleInputChange("opco_name", e.target.value)} /></div>
              <div><label className={LABEL_CLS}>OPCO ID</label><input type="text" className={INPUT_CLS} placeholder="OPCO ID" value={filters.opco_id || ""} onChange={e => handleInputChange("opco_id", e.target.value)} /></div>
            </div>
          )}

          {/* Dates */}
          {filterTab === "dates" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[["Created Date", "created_from", "created_to"], ["PR Date", "pr_date_from", "pr_date_to"], ["PO Date", "po_date_from", "po_date_to"]].map(([label, from, to]) => (
                <div key={label} className="space-y-3">
                  <h3 className="text-base font-medium text-gray-800">{label}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className={LABEL_CLS}>From</label><input type="date" className={INPUT_CLS} value={filters[from as keyof Filters] || ""} onChange={e => handleInputChange(from as keyof Filters, e.target.value)} {...dateRangeInputBounds(filters[from as keyof Filters], filters[to as keyof Filters]).from} /></div>
                    <div><label className={LABEL_CLS}>To</label><input type="date" className={INPUT_CLS} value={filters[to as keyof Filters] || ""} onChange={e => handleInputChange(to as keyof Filters, e.target.value)} {...dateRangeInputBounds(filters[from as keyof Filters], filters[to as keyof Filters]).to} /></div>
                  </div>
                </div>
              ))}
              <div className="space-y-3">
                <h3 className="text-base font-medium text-gray-800">PO/PR Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className={LABEL_CLS}>PR Number</label><input type="text" className={INPUT_CLS} placeholder="PR Number" value={filters.pr_number || ""} onChange={e => handleInputChange("pr_number", e.target.value)} /></div>
                  <div><label className={LABEL_CLS}>PO Number</label><input type="text" className={INPUT_CLS} placeholder="PO Number" value={filters.po_number || ""} onChange={e => handleInputChange("po_number", e.target.value)} /></div>
                </div>
              </div>
            </div>
          )}

          {/* Assignments */}
          {filterTab === "assignments" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div><label className={LABEL_CLS}>Site Engineer</label>
                <SearchableSelect
                  value={selectedSiteEngineer}
                  isMulti={false}
                  onChange={(opt: any) => setSelectedSiteEngineer(opt || null)}
                  options={availableSiteEngineers}
                  placeholder="Search site engineer..."
                  minSearchLength={0}
                />
              </div>
              <div><label className={LABEL_CLS}>Pending Assignment</label>
                <select className={SELECT_CLS} value={filters.pending_assignment || ""} onChange={e => handleInputChange("pending_assignment", e.target.value)}>
                  <option value="">All</option><option value="engineer">Engineer</option><option value="ta">TA</option>
                  <option value="mis">MIS</option><option value="report_designer">Report Maker</option>
                  <option value="validator">Validator</option><option value="team_coordinator">Team Coordinator</option>
                </select>
              </div>
              <div><label className={LABEL_CLS}>Technician Name</label><input type="text" className={INPUT_CLS} placeholder="Technician Name" value={filters.technician_name || ""} onChange={e => handleInputChange("technician_name", e.target.value)} /></div>
              <div><label className={LABEL_CLS}>Technician Number</label><input type="text" className={INPUT_CLS} placeholder="Technician Number" value={filters.technician_number || ""} onChange={e => handleInputChange("technician_number", e.target.value)} /></div>
              <div><label className={LABEL_CLS}>FSE Name</label><input type="text" className={INPUT_CLS} placeholder="FSE Name" value={filters.fse_name || ""} onChange={e => handleInputChange("fse_name", e.target.value)} /></div>
              <div><label className={LABEL_CLS}>FSE Number</label><input type="text" className={INPUT_CLS} placeholder="FSE Number" value={filters.fse_number || ""} onChange={e => handleInputChange("fse_number", e.target.value)} /></div>
              <div><label className={LABEL_CLS}>AOM Name</label><input type="text" className={INPUT_CLS} placeholder="AOM Name" value={filters.aom_name || ""} onChange={e => handleInputChange("aom_name", e.target.value)} /></div>
              <div><label className={LABEL_CLS}>AOM Number</label><input type="text" className={INPUT_CLS} placeholder="AOM Number" value={filters.aom_number || ""} onChange={e => handleInputChange("aom_number", e.target.value)} /></div>
            </div>
          )}

          {/* Status */}
          {filterTab === "status" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className={LABEL_CLS}>Site IDs (search multiple)</label>
                <input type="text" className={INPUT_CLS} placeholder="Enter Site IDs separated by space or comma"
                  value={filters.site_id_list || ""} onChange={e => handleInputChange("site_id_list", e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className={LABEL_CLS} style={{ marginBottom: 0 }}>Projects (select multiple)</label>
                  {availableProjects.length > 0 && (
                    <button type="button"
                      onClick={() => {
                        setSelectedProjects(availableProjects.map(p => ({ value: p.value, label: p.label })))
                        setFilters(prev => ({ ...prev, project_id: availableProjects.map(p => p.value).join(",") }))
                      }}
                      className="text-xs text-blue-700 hover:text-blue-900 border border-blue-300 rounded px-2 py-1 hover:bg-blue-50">
                      Select All Visible ({availableProjects.length})
                    </button>
                  )}
                </div>
                <SearchableSelect
                  value={selectedProjects}
                  isMulti
                  onChange={(opts: any) => {
                    const arr = opts || []
                    setSelectedProjects(arr)
                    setFilters(p => ({ ...p, project_id: arr.length > 0 ? arr.map((o: any) => o.value).join(",") : undefined }))
                  }}
                  options={availableProjects}
                  placeholder="Search and select projects (3+ chars)"
                  minSearchLength={3}
                />
              </div>
              <div><label className={LABEL_CLS}>Activity</label>
                <SelectWithSearch2 placeholder="Search activity (3+ chars)" value={selectedActivityStatus}
                  onChange={o => { setSelectedActivityStatus(o); setFilters(p => ({ ...p, activity_id: o ? o.value.toString() : undefined })) }}
                  apiEndpoint="/activities/list" dataKey="activities" labelKey="activity_name" valueKey="id" minSearchLength={3} className="w-full" />
              </div>
              <div><label className={LABEL_CLS}>Site Priority</label>
                <select className={SELECT_CLS} value={filters.site_priority || ""} onChange={e => handleInputChange("site_priority", e.target.value)}>
                  <option value="">All</option><option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select>
              </div>
              <div><label className={LABEL_CLS}>Stage Group</label>
                <select className={SELECT_CLS} value={filters.stage_group || ""} onChange={e => handleStageGroupChange(e.target.value)}>
                  <option value="">Select Stage Group</option>
                  <option value="survey_pending">Survey Pending</option><option value="survey_done">Survey Done</option>
                  <option value="field_work_approved">Field Work Approved</option><option value="report_completed">Report Completed</option>
                  <option value="report_finalized">Report Finalized</option><option value="report_preparation_pending">Report Preparation Pending</option>
                  <option value="validation_pending">Validation Pending</option><option value="report_submission_pending">Report Submission Pending</option>
                </select>
              </div>
              <div><label className={LABEL_CLS}>Workflow Status</label>
                <SelectWithSearch2 placeholder="Select Workflow Status" value={selectedWorkflowStatus}
                  onChange={o => { setSelectedWorkflowStatus(o); if (o) setFilters(p => ({ ...p, stage_group: undefined, workflow_status_list: undefined, survey_workflow_status: o.value.toString() })); else setFilters(p => ({ ...p, survey_workflow_status: undefined })) }}
                  options={WORKFLOW_STATUS_OPTIONS} className="w-full" />
              </div>
            </div>
          )}
        </div>

        {/* Location + options row */}
        <div className="border-t border-gray-100 px-6 py-4 flex flex-wrap items-end gap-5">
          {/* My location (for radius filter) */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-600">My Location <span className="text-gray-400">(for radius filter &amp; distance)</span></label>
            <button type="button" onClick={handleDetectLocation} disabled={locationLoading}
              className="inline-flex items-center gap-2 rounded border border-blue-600 bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60">
              {locationLoading ? <FiLoader className="h-3.5 w-3.5 animate-spin" /> : <FiTarget className="h-3.5 w-3.5" />}
              {locationLoading ? "Detecting…" : "Detect My Location"}
            </button>
            {reportingLocationLabel && (
              <span className="text-xs text-cyan-700 font-medium">📍 {reportingLocationLabel}</span>
            )}
            {currentLat && currentLng && !reportingLocationLabel && (
              <span className="text-xs text-green-700 font-medium">✓ {parseFloat(currentLat).toFixed(5)}, {parseFloat(currentLng).toFixed(5)}</span>
            )}
            {locationAddress && <p className="text-xs text-gray-500 max-w-xs truncate">{locationAddress}</p>}
            {locationError && <p className="text-xs text-red-600">{locationError}</p>}
          </div>

          {/* Radius toggle */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-600">Radius Filter</label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={useRadius} onChange={e => setUseRadius(e.target.checked)} className="rounded" />
              Apply radius filter
            </label>
            {useRadius && (
              <div className="flex items-center gap-2 mt-0.5">
                <input type="range" min={5} max={500} step={5} value={radiusKm} onChange={e => setRadiusKm(Number(e.target.value))} className="w-28 accent-blue-800" />
                <span className="text-xs text-gray-600 w-14">{radiusKm} km</span>
              </div>
            )}
          </div>

          {/* Site filters */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-600">Site Filters</label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><input type="checkbox" checked={onlyAssigned} onChange={e => setOnlyAssigned(e.target.checked)} className="rounded" /> Only assigned</label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><input type="checkbox" checked={onlyWorkable} onChange={e => setOnlyWorkable(e.target.checked)} className="rounded" /> Workable stages only</label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><input type="checkbox" checked={includeIncSites} onChange={e => setIncludeIncSites(e.target.checked)} className="rounded" /> Include INC sites</label>
          </div>

          <div className="ml-auto">
            <button type="button" onClick={handleLoad} disabled={loading}
              className="inline-flex items-center gap-2 rounded bg-blue-800 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-60">
              {loading ? <FiRefreshCw className="h-4 w-4 animate-spin" /> : <FiSearch className="h-4 w-4" />}
              {loading ? "Loading…" : "Load Sites"}
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {loaded && (
        <div className="px-6 py-5 space-y-4">
          {/* Summary bar */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded border border-gray-200 bg-white px-4 py-2 text-center shadow-sm">
              <div className="text-lg font-bold text-gray-900">{sites.length}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="rounded border border-gray-200 bg-white px-4 py-2 text-center shadow-sm">
              <div className="text-lg font-bold text-blue-800">{filteredSites.length}</div>
              <div className="text-xs text-gray-500">On map{anchor && useRadius ? ` / ≤${radiusKm}km` : ""}</div>
            </div>
            {sitesNoGps.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 px-4 py-2 text-center shadow-sm">
                <div className="text-lg font-bold text-amber-700">{sitesNoGps.length}</div>
                <div className="text-xs text-amber-600">No GPS</div>
              </div>
            )}
            {filters.stage_group && (
              <div className="flex items-center rounded border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-800">
                {STAGE_LABELS[filters.stage_group] || filters.stage_group}
              </div>
            )}

            {/* View toggle */}
            <div className="flex rounded border border-gray-200 overflow-hidden ml-auto">
              <button type="button" onClick={() => setViewMode("map")}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium ${viewMode === "map" ? "bg-blue-800 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
                <FiMap className="h-4 w-4" /> Map
              </button>
              <button type="button" onClick={() => setViewMode("list")}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-l border-gray-200 ${viewMode === "list" ? "bg-blue-800 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
                <FiList className="h-4 w-4" /> List
              </button>
            </div>

            {/* Open in Google Maps (all sites, coordinate-based) */}
            {googleMapsUrl && filteredSites.length > 0 && (
              <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700">
                <FiNavigation className="h-4 w-4" /> Open All in Google Maps
                <FiExternalLink className="h-3.5 w-3.5 opacity-80" />
              </a>
            )}
          </div>

          {/* ── MAP VIEW ── */}
          {viewMode === "map" && (
            <div className="rounded border border-gray-200 bg-white shadow-sm overflow-hidden">

              {/* ── Layer controls ── */}
              <div className="border-b border-gray-200 bg-white px-4 py-3">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                    <FiLayers className="h-3.5 w-3.5" /> Layers
                  </span>

                  {/* Sites */}
                  <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-gray-700">
                    <input type="checkbox" className="rounded accent-blue-600" checked={showSiteLayer} onChange={e => setShowSiteLayer(e.target.checked)} />
                    <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: "#2563eb" }} />
                    Sites
                    <span className="text-gray-400">
                      ({surveySiteCount} survey{incSiteCount > 0 ? ` · ${incSiteCount} INC` : ""})
                    </span>
                  </label>

                  {/* Engineer reporting locations */}
                  <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-gray-700">
                    <input type="checkbox" className="rounded accent-purple-600" checked={showReportingLayer}
                      onChange={e => {
                        setShowReportingLayer(e.target.checked)
                        if (e.target.checked && !attendanceLoaded) fetchAttendance()
                      }} />
                    <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: "#9333ea" }} />
                    Engineer Reporting Locations
                    {attendanceLoaded && (
                      <span className="text-gray-400">
                        ({attendanceData.filter(a => toNum(a.user?.reporting_latitude ?? a.reporting_latitude) !== null).length})
                      </span>
                    )}
                  </label>

                  {/* Today's check-in */}
                  <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-gray-700">
                    <input type="checkbox" className="rounded accent-emerald-600" checked={showCheckInLayer}
                      onChange={e => {
                        setShowCheckInLayer(e.target.checked)
                        if (e.target.checked && !attendanceLoaded) fetchAttendance()
                      }} />
                    <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: "#059669" }} />
                    Today's Check-In
                    {attendanceLoaded && (
                      <span className="text-gray-400">
                        ({attendanceData.filter(a => toNum(a.check_in_latitude) !== null).length})
                      </span>
                    )}
                  </label>

                  {/* Today's check-out */}
                  <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-gray-700">
                    <input type="checkbox" className="rounded accent-rose-600" checked={showCheckOutLayer}
                      onChange={e => {
                        setShowCheckOutLayer(e.target.checked)
                        if (e.target.checked && !attendanceLoaded) fetchAttendance()
                      }} />
                    <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: "#e11d48" }} />
                    Today's Check-Out
                    {attendanceLoaded && (
                      <span className="text-gray-400">
                        ({attendanceData.filter(a => toNum(a.check_out_latitude) !== null).length})
                      </span>
                    )}
                  </label>

                  {attendanceLoading && (
                    <span className="flex items-center gap-1.5 text-xs text-blue-600">
                      <FiLoader className="h-3 w-3 animate-spin" /> Loading attendance…
                    </span>
                  )}

                  {attendanceLoaded && !attendanceLoading && (
                    <button type="button" onClick={fetchAttendance}
                      className="ml-auto flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 transition-colors">
                      <FiRefreshCw className="h-3 w-3" /> Refresh
                    </button>
                  )}
                </div>
              </div>

              {/* ── Site legend ── */}
              <div className="flex flex-wrap items-center gap-4 border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-600">
                <span className="font-medium text-gray-700">Sites:</span>
                {[
                  { label: "Site Created", color: "#94a3b8" },
                  { label: "Engineer Assigned", color: "#06b6d4" },
                  { label: "Field Work", color: "#f97316" },
                  { label: "TA Review", color: "#8b5cf6" },
                  { label: "Report Maker", color: "#f59e0b" },
                  { label: "Validation", color: "#0d9488" },
                  { label: "Rejected", color: "#dc2626" },
                  { label: "Finalized", color: "#f43f5e" },
                  { label: "On Hold", color: HOLD_COLOR },
                ].map(l => (
                  <span key={l.label} className="flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />
                    {l.label}
                  </span>
                ))}
                <span className="mx-2 text-gray-300">|</span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: INC_DEFAULT_COLOR }} />
                  INC Site (square pin)
                </span>
                <span className="mx-2 text-gray-300">|</span>
                <span className="font-medium text-gray-700">Layers:</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#9333ea" }} />Reporting</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#059669" }} />Check-In</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#e11d48" }} />Check-Out</span>
                <span className="ml-auto text-gray-400">Click any pin for details</span>
              </div>

              {filteredSites.length === 0 && !showReportingLayer && !showCheckInLayer && !showCheckOutLayer ? (
                <div className="flex items-center justify-center h-96 text-sm text-gray-400">
                  No sites with GPS data. Adjust filters, disable radius, or enable an attendance layer.
                </div>
              ) : (
                <div ref={mapContainerRef} className="relative" style={{ height: isFullscreen ? "100vh" : 580, width: "100%" }}>
                  <button
                    onClick={toggleFullscreen}
                    title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                    className="absolute top-2 right-2 z-[400] bg-white border border-gray-300 rounded p-1.5 shadow hover:bg-gray-50"
                  >
                    {isFullscreen ? <FiMinimize2 className="w-4 h-4 text-gray-700" /> : <FiMaximize2 className="w-4 h-4 text-gray-700" />}
                  </button>
                  <div ref={mapDivRef} style={{ height: "100%", width: "100%" }} />
                </div>
              )}

              {filteredSites.length > 0 && (
                <div className="border-t border-gray-100 px-4 py-2.5 text-xs text-gray-500 flex items-center gap-2">
                  <FiMapPin className="h-3.5 w-3.5 text-blue-700" />
                  {filteredSites.length} site{filteredSites.length !== 1 ? "s" : ""} — each pin at exact GPS coordinate.
                  {googleMapsUrl && (
                    <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer"
                      className="ml-auto inline-flex items-center gap-1 text-green-700 font-medium hover:underline">
                      <FiExternalLink className="h-3.5 w-3.5" /> Open in Google Maps
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── LIST VIEW ── */}
          {viewMode === "list" && (() => {
            const listSites = sites.filter(s => s.status !== "cancelled")
            const colCount = 17

            // Build lookup maps from attendanceData for quick access
            const engReportingByUserId: Record<number, { lat: number; lng: number }> = {}
            const engCheckInByUserId: Record<number, { lat: number; lng: number }> = {}
            attendanceData.forEach(rec => {
              if (!rec.user) return
              const uid = rec.user.id
              const rLat = toNum(rec.user.reporting_latitude ?? rec.reporting_latitude)
              const rLng = toNum(rec.user.reporting_longitude ?? rec.reporting_longitude)
              if (rLat !== null && rLng !== null && !(rLat === 0 && rLng === 0)) engReportingByUserId[uid] = { lat: rLat, lng: rLng }
              const cLat = toNum(rec.check_in_latitude)
              const cLng = toNum(rec.check_in_longitude)
              if (cLat !== null && cLng !== null && !(cLat === 0 && cLng === 0)) engCheckInByUserId[uid] = { lat: cLat, lng: cLng }
            })

            return (
            <div className="overflow-hidden rounded border border-gray-200 bg-white shadow-sm">
              {!attendanceLoaded && (
                <div className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
                  <FiLoader className="h-3 w-3" />
                  Enable an attendance layer above to populate distance-to-engineer columns.
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left">Site ID</th>
                      <th className="px-4 py-3 text-left">Site Type</th>
                      <th className="px-4 py-3 text-left">Site Name</th>
                      <th className="px-4 py-3 text-left">Project</th>
                      <th className="px-4 py-3 text-left">Activity</th>
                      <th className="px-4 py-3 text-left">Stage</th>
                      <th className="px-4 py-3 text-left whitespace-nowrap">Created On</th>
                      <th className="px-4 py-3 text-left">Work Status</th>
                      <th className="px-4 py-3 text-left">Priority</th>
                      <th className="px-4 py-3 text-left">Engineer</th>
                      <th className="px-4 py-3 text-left">Technician</th>
                      <th className="px-4 py-3 text-left">FSE</th>
                      <th className="px-4 py-3 text-left">District</th>
                      <th className="px-4 py-3 text-left">Site Coordinates</th>
                      <th className="px-4 py-3 text-left whitespace-nowrap">Dist to Eng. Reporting</th>
                      <th className="px-4 py-3 text-left whitespace-nowrap">Dist to Check-in</th>
                      <th className="px-4 py-3 text-left">Open</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {listSites.map(site => {
                      const coords = getSiteCoords(site)
                      const engId = site.site_engineer_userid ?? site.site_engineer?.id ?? null
                      const reportingLoc = engId ? engReportingByUserId[engId] ?? null : null
                      const checkInLoc = engId ? engCheckInByUserId[engId] ?? null : null
                      const distReporting = coords && reportingLoc
                        ? haversineKm(coords.lat, coords.lng, reportingLoc.lat, reportingLoc.lng) : null
                      const distCheckIn = coords && checkInLoc
                        ? haversineKm(coords.lat, coords.lng, checkInLoc.lat, checkInLoc.lng) : null
                      return (
                        <tr key={`${site.site_type || "survey"}-${site.id}`} className={isOnHold(site) ? "bg-gray-100 hover:bg-gray-200" : "hover:bg-gray-50"}>
                          <td className="px-4 py-3 font-mono text-xs font-semibold text-blue-800">{site.site_id}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${isIncSite(site) ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
                              {siteTypeLabel(site)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-900 max-w-[160px] truncate">{site.site_name || site.site_address || "—"}</td>
                          <td className="px-4 py-3 text-xs text-gray-600 max-w-[160px] truncate">{site.project?.project_name || "—"}</td>
                          <td className="px-4 py-3 text-xs text-gray-600 max-w-[130px] truncate">{site.activity?.activity_name || "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold text-white ${STAGE_DOT_COLORS[site.survey_workflow_status] || "bg-gray-400"}`}>
                              {fmtStage(site.survey_workflow_status)}
                            </span>
                            {isOnHold(site) && (
                              <span className="ml-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold text-white" style={{ background: HOLD_COLOR }}>
                                On Hold
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{fmtCreatedDate(site.created_at)}</td>
                          <td className="px-4 py-3">
                            {site.site_engineer_work_status ? (
                              <span className="inline-block rounded px-2 py-0.5 text-[10px] font-semibold capitalize bg-indigo-100 text-indigo-700">
                                {site.site_engineer_work_status.replace(/_/g, " ")}
                              </span>
                            ) : "—"}
                          </td>
                          <td className="px-4 py-3">
                            {site.site_priority && (
                              <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-semibold text-white capitalize ${PRIORITY_COLORS[site.site_priority] || "bg-gray-400"}`}>
                                {site.site_priority}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-700 text-xs">
                            {site.site_engineer ? `${site.site_engineer.first_name} ${site.site_engineer.last_name}` : "—"}
                          </td>
                          <td className="px-4 py-3 text-gray-700 text-xs">{site.technician_name || "N/A"}</td>
                          <td className="px-4 py-3 text-gray-700 text-xs">{site.fse_name || "N/A"}</td>
                          <td className="px-4 py-3 text-gray-700 text-xs whitespace-nowrap">{site.district?.name || "—"}</td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-500">
                            {coords ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}` : <span className="text-amber-500">No GPS</span>}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">
                            {distReporting !== null ? (
                              <span className="text-blue-700 font-medium">{distReporting.toFixed(1)} km</span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">
                            {distCheckIn !== null ? (
                              <span className="text-green-700 font-medium">{distCheckIn.toFixed(1)} km</span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {coords && (
                              <a href={siteGoogleMapsUrl(coords.lat, coords.lng)} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded bg-green-50 px-2 py-1 text-xs text-green-700 hover:bg-green-100"
                                title="Opens at exact GPS coordinate in Google Maps">
                                <FiMapPin className="h-3 w-3" /> Maps
                              </a>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                    {listSites.length === 0 && (
                      <tr>
                        <td colSpan={colCount} className="py-12 text-center text-sm text-gray-500">
                          No sites found for the applied filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            )
          })()}
        </div>
      )}

      {/* Engineer assignment — opened from a pin's popup. Portalled into the
          fullscreen element while the map is fullscreen: the browser renders
          nothing outside that element, so the dialog used to open invisibly
          behind the map and assigning looked broken. */}
      {assignSite && createPortal(
        <>
          <div className="fixed inset-0 z-[1100] bg-black bg-opacity-50" onClick={() => setAssignSite(null)} />
          <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded bg-white shadow-xl">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {assignSite.site_engineer_userid ? "Reassign" : "Assign"} Engineer
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {assignSite.site_id}
                  {assignSite.site_name ? ` · ${assignSite.site_name}` : ""}
                  {assignSite.project?.project_name ? ` · ${assignSite.project.project_name}` : ""}
                </p>
                {assignSite.site_engineer && (
                  <p className="mt-1 text-xs text-gray-500">
                    Currently: {assignSite.site_engineer.first_name} {assignSite.site_engineer.last_name}
                  </p>
                )}

                {getSiteCoords(assignSite) && (
                  <div className="mt-4">
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Suggested — closest to site
                    </div>
                    {suggestionsLoading ? (
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <FiLoader className="h-3 w-3 animate-spin" /> Finding nearby engineers…
                      </div>
                    ) : engineerSuggestions.length === 0 ? (
                      <div className="text-xs text-gray-400">No engineers available for this project.</div>
                    ) : (
                      <ul className="divide-y divide-gray-100 rounded border border-gray-200">
                        {engineerSuggestions.map(eng => {
                          const isCurrent = eng.id === assignSite.site_engineer_userid
                          return (
                            <li key={eng.id}>
                              <button
                                type="button"
                                disabled={assigning || isCurrent}
                                onClick={() => handleAssignEngineer({ id: eng.id, value: eng.id, label: eng.label })}
                                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <span className="truncate text-gray-800">
                                  {eng.label}
                                  {isCurrent && <span className="ml-1 text-xs text-gray-400">(current)</span>}
                                </span>
                                <span className={`shrink-0 text-xs font-medium ${eng.distance_km !== null ? "text-blue-700" : "text-gray-400"}`}>
                                  {eng.distance_km !== null ? `${eng.distance_km.toFixed(1)} km` : "No reporting location"}
                                </span>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                )}

                <div className="mt-4">
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Or search all engineers
                  </div>
                  {/* Scoped to the site's own project, so the list is the
                      engineers who can actually work it. */}
                  <SelectWithSearch2
                    placeholder="Select Engineer"
                    value={null}
                    onChange={(option: any) => option && handleAssignEngineer(option)}
                    apiEndpoint="/survey/sites/assignment/users"
                    extraParams={{
                      role_id: SITE_ENGINEER_ASSIGNABLE_ROLE_IDS,
                      project_id: assignSite.project_id,
                      src: "all",
                      exclude_user_id: user?.id,
                      // Nearest-first ordering, same as the suggestions above.
                      near_latitude: getSiteCoords(assignSite)?.lat,
                      near_longitude: getSiteCoords(assignSite)?.lng,
                    }}
                    mapOption={(u: any) => ({
                      id: u.id,
                      value: u.id,
                      label: `${u.first_name} ${u.last_name} (${u.username}) - ${u?.employee_code || "N/A"}${typeof u.distance_km === "number" ? ` · ${u.distance_km.toFixed(1)} km` : ""}`,
                    })}
                    className="w-full"
                  />
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setAssignSite(null)}
                    disabled={assigning}
                    className="px-4 py-2 text-gray-600 transition-colors hover:text-gray-800 disabled:opacity-50"
                  >
                    {assigning ? "Saving..." : "Cancel"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>,
        (isFullscreen && mapContainerRef.current) || document.body,
      )}

      {/* Empty state */}
      {!loaded && !loading && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FiFilter className="mb-3 h-10 w-10 text-gray-300" />
          <p className="text-base font-medium text-gray-500">Apply filters and click <strong>Load Sites</strong></p>
          <p className="mt-1 text-sm text-gray-400">Sites will appear on the embedded map — click any pin to see site details.</p>
        </div>
      )}
    </div>
  )
}

export default MapViewPage
